import React, { useMemo } from 'react';
import { TeamChallengeTaskProgress } from '@/src/types/JamTeamChallengeAnswer';
import { Box, ColumnLayout, Container, ExpandableSection } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { orderBy } from 'lodash';
import { getTaskCluePointPenalty, getTaskScore } from '@/src/utils/task-score.utils';

interface ShowTasksProps {
  taskProgress: TeamChallengeTaskProgress[];
}

const ShowTasks: React.FC<ShowTasksProps> = ({ taskProgress }) => {
  const { t } = useTranslation();
  const taskDetailsSorted = useMemo(() => orderBy(taskProgress, 'taskNumber'), [taskProgress]);

  return (
    <ExpandableSection
      headerText={t(i18nKeys.facilitator.participants.label.showTask)}
      data-testid="progress-show-tasks-expandable"
    >
      {taskDetailsSorted.map((task) => (
        <Box key={task.taskId} variant="h4" margin={{ vertical: 'xxxs' }}>
          <Container data-testid={`task-${task.taskNumber}-container`}>
            <Box variant="h4" margin={{ bottom: 'xs' }}>
              {t(i18nKeys.facilitator.participants.label.task)} {task.taskNumber}
            </Box>
            <ColumnLayout borders="horizontal" columns={2}>
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.completed)}</Box>
                <Box data-testid="task-complete">
                  {task.completedTime ? t(i18nKeys.general.yes) : t(i18nKeys.general.no)}
                </Box>
              </Box>
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.pointsEarned)}</Box>
                <Box data-testid="task-points-earned">{task.pointsEarned ?? 0}</Box>
              </Box>
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.usedClues)}</Box>
                <Box data-testid="clues-used-count">{task.cluePenalties?.length ?? 0}</Box>
              </Box>
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.cluePointPenalty)}</Box>
                <Box data-testid="task-clue-point-penalty">{getTaskCluePointPenalty(task)}</Box>
              </Box>
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.totalPoints)}</Box>
                <Box data-testid="task-total-points">{`${getTaskScore(task)} / ${task.pointsPossible ?? 0}`}</Box>
              </Box>
              {!!task?.cluePenalties?.length && task.cluePenalties.some((cp) => cp.refunded) && (
                <Box className="space-between">
                  <Box>{t(i18nKeys.facilitator.participants.label.refundedClues)}</Box>
                  <Box data-testid="clues-refunded-count">
                    {task.cluePenalties.filter((cluePenalty) => cluePenalty.refunded).length}
                  </Box>
                </Box>
              )}
              <Box className="space-between">
                <Box>{t(i18nKeys.facilitator.participants.label.submittedAnswers)}</Box>
                <Box data-testid="submitted-answers-count">{(task.submittedAnswers || []).length}</Box>
              </Box>
            </ColumnLayout>
          </Container>
        </Box>
      ))}
    </ExpandableSection>
  );
};

export default ShowTasks;
